import { isHttpClientError, type HttpClientError } from "../../../lib/http/errors";

const graphValidationMessage = "有些行程安排需要检查，请打开“骨架编辑”，按顶部提示调整后再试。如果看不出哪里有问题，请联系管理员。";
const referenceMessage = "有些地点和交通安排对不上。请打开“骨架编辑”，检查所选地点，以及每段交通的出发地和目的地后再试。";
const aiResultMessage = "自动补写的内容与当前行程对不上，原来的内容还在。请重新生成；如果还是失败，请联系管理员。";
const dateMessage = "行程日期没有排对。请打开“骨架编辑”，检查开始日期、每天的日期和行程天数后再试。";
const timeMessage = "交通时间没有填对。请打开“骨架编辑”，检查出发和到达的日期、时间后再试。";
const railwayOvernightMessages: Readonly<Record<string, string>> = {
  "railway-overnight-range-invalid": "这趟火车的出发、到达日期，与安排乘车的天数对不上。请检查火车日期和行程天数。",
  "railway-overnight-stay-missing": "这趟火车需要在车上过夜，但有一天还没有安排住宿。请检查乘车这几天的“火车上”住宿。",
  "railway-overnight-stay-order-invalid": "这趟火车的“火车上”住宿位置，与现在的行程顺序对不上。请查看乘车这几天的安排。",
  "railway-overnight-stay-invalid": "这趟火车原来对应的“火车上”住宿已被删除或改成了其他地点。请检查这几天的住宿安排。",
  "railway-overnight-stay-reference-invalid": "这趟火车的几处“火车上”住宿选的不是同一个选项。请检查并选用同一个“火车上”住宿选项。",
};

/** 保存与文案请求共用跨夜火车的通俗说明，不向普通提示输出内部原因码。 */
export function itineraryRailwayOvernightFailureMessage(reason: string): string | undefined {
  return Object.hasOwn(railwayOvernightMessages, reason) ? railwayOvernightMessages[reason] : undefined;
}
type ItineraryFailureContext = "content" | "load" | "save";
const persistedFailureOutcomes: Record<ItineraryFailureContext, string> = {
  content: "本次文案未生成，当前正文未被替换",
  load: "行程暂时无法加载",
  save: "本次尚未保存，当前编辑内容未被替换",
};
const persistedSegmentDetails = new Set([
  "persisted-segment-endpoint-unmatched", "persisted-segment-endpoint-ambiguous",
  "persisted-segment-endpoint-invalid", "persisted-segment-node-reference-invalid",
]);

const graphDetailMessages: Readonly<Record<string, string>> = {
  "itinerary-date-header-incomplete": dateMessage,
  "day-date-without-itinerary-date": dateMessage,
  "itinerary-date-range-invalid": dateMessage,
  "itinerary-date-count-invalid": dateMessage,
  "itinerary-day-date-invalid": dateMessage,
  "node-time-range-invalid": "有一处安排的开始、结束时间没有填对。请打开“骨架编辑”，检查这个地点的计划时间后再试。",
  "segment-time-range-invalid": timeMessage,
  "segment-duration-inconsistent": "填写的交通时长，与出发到到达之间的时间对不上。请打开“骨架编辑”，检查这段交通的时间后再试。",
  disconnected: "有两个地点之间还没有安排交通。请打开“骨架编辑”检查并补充；如果是旧行程且无法修改，请联系管理员。",
  "segment-endpoints-not-adjacent": referenceMessage,
  "segment-day-reference-invalid": referenceMessage,
  "duplicate-node-id": referenceMessage,
  "node-sort-order-not-contiguous": referenceMessage,
  "node-resource-required": referenceMessage,
  "node-title-required": referenceMessage,
  "duplicate-segment-id": referenceMessage,
  "duplicate-segment-route-seq": referenceMessage,
  "parallel-track-node-coverage-invalid": referenceMessage,
  "parallel-track-segment-coverage-invalid": referenceMessage,
  "parallel-track-segment-duplicate": referenceMessage,
  "intermediate-node-must-be-point": referenceMessage,
  "driving-endpoint-reference-invalid": referenceMessage,
  "node-type-reference-invalid": referenceMessage,
  "node-point-type-invalid": referenceMessage,
  "point-description-node-not-in-itinerary": referenceMessage,
  "point-description-day-not-in-itinerary": referenceMessage,
  "point-description-point-invalid": referenceMessage,
  "parallel-track-node-invalid": referenceMessage,
  "parallel-track-segment-invalid": referenceMessage,
  "railway-station-reference-invalid": referenceMessage,
  "railway-region-reference-invalid": referenceMessage,
  "transport-spu-endpoints-invalid": referenceMessage,
  "rail-endpoint-kind-invalid": referenceMessage,
  "air-endpoint-kind-invalid": referenceMessage,
  "day-id-belongs-to-another-itinerary": referenceMessage,
  "node-id-belongs-to-another-itinerary": referenceMessage,
  "segment-id-belongs-to-another-itinerary": referenceMessage,
  "ai-result-day-invalid": aiResultMessage,
  "ai-result-day-mismatch": aiResultMessage,
  "ai-result-scope-invalid": aiResultMessage,
  "ai-result-summary-slot-invalid": aiResultMessage,
  "ai-result-node-invalid": aiResultMessage,
  "ai-result-segment-invalid": aiResultMessage,
};

const codeMessages: Readonly<Record<string, string>> = {
  ITINERARY_AI_CONFIG_MISSING: "自动补写功能还没有设置好，请联系管理员处理后再试。",
  ITINERARY_AI_AUTHENTICATION_FAILED: "系统暂时无法使用自动补写功能，请联系管理员检查后再试。",
  ITINERARY_AI_TIMEOUT: "自动补写花的时间太长，原来的内容还在，请稍后再试。",
  ITINERARY_AI_RATE_LIMITED: "自动补写服务正忙，或可用次数不足。请稍后再试；一直失败时请联系管理员。",
  ITINERARY_AI_UPSTREAM: "自动补写暂时用不了，原来的内容还在。请稍后再试；一直失败时请联系管理员。",
  ITINERARY_AI_INVALID_OUTPUT: "这次没有生成可用的行程介绍，原来的内容还在。请重新生成；如果还是失败，请联系管理员。",
  ITINERARY_READONLY: "该行程已定稿或归档，无法重写，请先确认行程是否允许编辑。",
  ITINERARY_REVISION_CONFLICT: "行程已被其他人更新，请先保存或备份当前草稿，再刷新后重试。",
};

/** 仅接收对象结构，不把数组或正文当作诊断字段。 */
function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

/** 清除控制字符并限制诊断文本长度，避免污染页面提示。 */
function diagnosticText(value: unknown, limit = 80): string {
  if (typeof value !== "string") return "";
  const clean = value.replace(/\p{C}/gu, "").trim();
  return clean.length > limit ? `${clean.slice(0, limit)}…` : clean;
}

/** 使用持久化错误自身的对象定位，不使用正在请求的日次猜测故障位置。 */
function persistedTimeSlotMessage(error: HttpClientError, context: ItineraryFailureContext): string {
  const data = asRecord(asRecord(error.responseBody)?.data);
  const dayNumber = data?.day_number;
  const day = typeof dayNumber === "number" && Number.isSafeInteger(dayNumber) && dayNumber > 0
    ? `第${dayNumber}天` : "";
  const nodeName = diagnosticText(data?.node_name);
  const node = nodeName ? `节点「${nodeName}」` : "节点";
  const actual = data?.actual_value === null ? "缺失"
    : typeof data?.actual_value === "string" ? diagnosticText(data.actual_value) || "空白" : "未提供";
  const identifiers = [
    ["itinerary_id", diagnosticText(data?.itinerary_id)],
    ["day_id", diagnosticText(data?.day_id)],
    ["node_id", diagnosticText(data?.node_id)],
    ["trace", diagnosticText(error.request.traceId)],
  ].filter(([, value]) => value).map(([key, value]) => `${key}=${value}`);
  const outcome = persistedFailureOutcomes[context];
  return `已保存行程${day}${node}的时段无法识别（time_slot=${actual}），${outcome}。请联系管理员核对并处理历史数据。诊断：reason=persisted-node-time-slot-invalid${identifiers.length ? `；${identifiers.join("；")}` : ""}。`;
}

/** 精确识别持久化图错误，供加载、文案和保存共享恢复指引，不判断草稿是否落盘。 */
export function itineraryPersistedGraphFailureMessage(
  error: HttpClientError, context: ItineraryFailureContext,
): string | undefined {
  if (error.statusCode !== 422
    || (error.code !== "ITINERARY_INVALID_GRAPH" && error.code !== "ITINERARY_REFERENCE_INVALID")) return undefined;
  if (error.message === "persisted-node-time-slot-invalid") return persistedTimeSlotMessage(error, context);
  if (!persistedSegmentDetails.has(error.message)) return undefined;
  const recovery = context === "load"
    ? "请联系管理员修复后再打开。"
    : "请到“骨架编辑”检查各段交通的出发地和目的地；无法修改或仍不能保存时，请联系管理员。";
  return `行程中有些地点和交通安排对不上，${persistedFailureOutcomes[context]}。${recovery}`;
}

/** 按稳定错误码和精确原因提供行程请求反馈，仅输出受限定位信息。 */
export function itineraryRequestFailureMessage(error: unknown, context: ItineraryFailureContext = "content"): string {
  if (isHttpClientError(error)) {
    if (error.code && Object.hasOwn(codeMessages, error.code)) return codeMessages[error.code];
    if (error.statusCode === 401) return "登录已失效，请重新登录后再使用 AI 重写。";
    if (error.statusCode === 403) return "当前账号没有 AI 重写所需的行程编辑权限，请联系管理员开通。";
    if (error.statusCode === 404) return "该行程不存在或已被删除，请返回行程列表重新选择。";
    if (error.statusCode === 429) return "请求过于频繁，请稍后再使用 AI 重写。";
    if (error.code === "ITINERARY_INVALID_GRAPH" || error.code === "ITINERARY_REFERENCE_INVALID") {
      const railwayMessage = itineraryRailwayOvernightFailureMessage(error.message);
      if (railwayMessage) return `${railwayMessage}${persistedFailureOutcomes[context]}。如果仍无法继续，请联系管理员。`;
      const persistedMessage = itineraryPersistedGraphFailureMessage(error, context);
      if (persistedMessage) return persistedMessage;
      if (Object.hasOwn(graphDetailMessages, error.message)) return graphDetailMessages[error.message];
      return context === "load" ? "这条行程之前保存的内容有问题，暂时打不开。请联系管理员处理后再打开。" : graphValidationMessage;
    }
    if (error.statusCode === 422) return "有些行程内容没有填全或没有填对，这次没有自动补写。请打开“骨架编辑”，按顶部提示检查后再试。";
    if (error.statusCode === 504 || error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") return "等了太久还没有收到自动补写的结果，原来的内容还在，请稍后再试。";
    if (error.statusCode === null) return "暂时连不上系统，原来的内容还在。请检查网络后再试。";
  } else if (error instanceof Error && (error.message.includes("请先点击保存") || error.message.startsWith("点位介绍") || error.message.startsWith("AI 未返回有效正文") || error.message.startsWith("未能生成本日摘要"))) {
    return error.message;
  }
  return "这次没有完成自动补写，原来的内容还在。请再试一次；如果还是失败，请联系管理员。";
}
